import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Users } from "lucide-react";

const STATS = [
  { value: 12, suffix: "+", label: "Leistungsbereiche" },
  { value: 100, suffix: "%", label: "Persönliche Betreuung" },
  { value: 1, suffix: "", label: "Standort. Fokus. Qualität." },
  { value: 0, suffix: "", label: "Versteckte Kosten" },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / 1500, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export const Hero = () => (
  <section id="hero" className="relative" data-testid="hero-section">
    <div className="relative h-[92vh] min-h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1513467535987-fd81bc7d62f8?q=80&w=1920&auto=format&fit=crop"
          alt="Zimmermannsarbeit im Spessart"
          className="kenburns h-full w-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(26,60,26,0.85) 0%, rgba(42,92,42,0.6) 35%, rgba(0,0,0,0.35) 100%)" }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-center">
        <motion.p
          className="font-condensed text-white/90 text-sm sm:text-base uppercase mb-4"
          style={{ letterSpacing: "3px" }}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="hero-overline"
        >
          Ihr Zimmerer- und Dachdeckermeister im Spessart
        </motion.p>

        <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="hero-headline">
          <motion.span className="block" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}>
            Mehr als Dächer.
          </motion.span>
          <motion.span className="block text-white/90" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}>
            Individueller Holzbau.
          </motion.span>
        </h1>

        <motion.p
          className="mt-6 max-w-xl text-white/85 text-base md:text-lg"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.8 }}
          data-testid="hero-subheadline"
        >
          Anbau · Aufstockung · Dachsanierung · Hallenbau · Photovoltaik · und vieles mehr.
          Von der Planung bis zur Fertigstellung.
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.0 }}
        >
          <a href="#kontakt" className="btn-green cta-pulse rounded-full px-8 py-3.5 font-semibold" data-testid="hero-cta-anfragen">
            Jetzt anfragen
          </a>
          <a href="#leistungen" className="btn-ghost-white rounded-full px-8 py-3.5 font-semibold" data-testid="hero-cta-leistungen">
            Unsere Leistungen
          </a>
        </motion.div>

        <motion.div
          className="mt-10 inline-flex items-center gap-2 self-start rounded-full bg-white/15 backdrop-blur-md border border-white/30 px-5 py-2.5 text-white text-sm"
          initial={{ opacity: 0, scale: 0.7, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.8, type: "spring", stiffness: 260, damping: 14 }}
          data-testid="hero-floating-badge"
        >
          <Users size={16} /> Familienunternehmen · Andreas &amp; Richard Müller
        </motion.div>
      </div>
    </div>

    <div className="bg-[#1A3C1A]" data-testid="hero-stats-bar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-y-8 py-10">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            className="text-left md:text-center md:border-r last:border-r-0 border-white/10 md:px-4"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <div className="font-heading text-4xl sm:text-5xl font-extrabold text-white">
              <Counter value={s.value} suffix={s.suffix} />
            </div>
            <div className="mt-1 font-condensed tracking-[0.15em] uppercase text-white/70 text-sm">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
